import { ApiError } from "./client";
import { sanitizeApiMessage } from "./sanitize";

const GENERIC_ERROR = "Não foi possível concluir a ação. Tente novamente.";

export const ORDER_NOT_FOUND =
  "Pedido indisponível. Volte à lista e escolha um pedido ativo.";
const QUOTATION_ITEM_NOT_FOUND =
  "Este item não está mais disponível. Recarregue a página e tente novamente.";
const STORE_NOT_FOUND = "Loja não encontrada. Selecione a loja novamente.";
const CHECKIN_CLOSED = "Conferência encerrada. Reabra para editar este item.";
const INVALID_PAYLOAD = "Confira os campos e tente novamente.";
const SESSION_EXPIRED = "Sessão expirada. Entre novamente.";
const INSUFFICIENT_PERMISSIONS = "Você não tem permissão para esta ação.";
const ACCOUNT_DEACTIVATED =
  "Conta desativada. Entre em contato com os administradores.";
const TOO_MANY_REQUESTS =
  "Muitas tentativas. Aguarde um instante e tente novamente.";
const INTERNAL_SERVER_ERROR = "Falha interna do servidor. Tente novamente.";
const LEGACY_DIRECT_KG_READ_ONLY =
  "Registro histórico de kg disponível somente para visualização.";
const LEGACY_KG_MIGRATION_TARGET_NOT_FOUND =
  "Item histórico de kg não encontrado. Recarregue a página e tente novamente.";
const LEGACY_KG_MIGRATION_INVALID_PAYLOAD =
  "Confira os valores inteiros da migração e tente novamente.";
const LEGACY_KG_MIGRATION_REQUIRED =
  "Este item histórico de kg precisa ser migrado antes desta ação.";
const STALE_RETURN_DECISION =
  "Esta devolução foi atualizada. Reabra a decisão e tente novamente.";
const QUOTATION_PUBLISHED_READ_ONLY =
  "Reabra a cotação antes de alterar uma cotação publicada.";

const CODE_MESSAGES: Record<string, string> = {
  order_not_found: ORDER_NOT_FOUND,
  quotation_item_not_found: QUOTATION_ITEM_NOT_FOUND,
  store_not_found: STORE_NOT_FOUND,
  checkin_closed: CHECKIN_CLOSED,
  history_undo_conflict:
    "Esta ação não pode ser desfeita enquanto houver movimentações dependentes. Atualize o histórico e tente novamente.",
  history_undo_closed: "Reabra a carga antes de desfazer esta ação.",
  box_movement_legacy_ambiguous:
    "Registro antigo sem vínculo confiável para desfazer.",
  box_movement_invalid: "Esta movimentação não pode ser desfeita.",
  box_movement_already_undone:
    "Esta movimentação já foi desfeita. Atualize o histórico.",
  box_movement_insufficient_balance:
    "Saldo insuficiente para desfazer esta movimentação.",
  box_movement_checkin_closed: "Reabra a carga antes de desfazer esta ação.",
  box_movement_not_found: "Movimentação não encontrada nesta carga.",
  box_movement_forbidden: "Você não tem permissão para desfazer caixas.",
  invalid_payload: INVALID_PAYLOAD,
  account_deactivated: ACCOUNT_DEACTIVATED,
  order_item_not_found: QUOTATION_ITEM_NOT_FOUND,
  legacy_direct_kg_read_only: LEGACY_DIRECT_KG_READ_ONLY,
  legacy_kg_migration_target_not_found: LEGACY_KG_MIGRATION_TARGET_NOT_FOUND,
  legacy_kg_migration_invalid_payload: LEGACY_KG_MIGRATION_INVALID_PAYLOAD,
  legacy_kg_migration_checkin_closed: CHECKIN_CLOSED,
  legacy_kg_migration_required: LEGACY_KG_MIGRATION_REQUIRED,
  stale_return_decision: STALE_RETURN_DECISION,
  quotation_published_read_only: QUOTATION_PUBLISHED_READ_ONLY,
};

const RAW_MESSAGES: Record<string, string> = {
  "order not found": ORDER_NOT_FOUND,
  "pedido não encontrado. recarregue a página.": ORDER_NOT_FOUND,
  "quotation item not found": QUOTATION_ITEM_NOT_FOUND,
  "quotation_item not found": QUOTATION_ITEM_NOT_FOUND,
  "store not found": STORE_NOT_FOUND,
  "checkin is encerrado and cannot be edited": CHECKIN_CLOSED,
  "invalid payload": INVALID_PAYLOAD,
  "missing request body": INVALID_PAYLOAD,
  "token is expired": SESSION_EXPIRED,
  "token expired": SESSION_EXPIRED,
  "invalid token": SESSION_EXPIRED,
  "invalid token claims": SESSION_EXPIRED,
  "not authenticated": SESSION_EXPIRED,
  unauthenticated: SESSION_EXPIRED,
  "missing authorization header": SESSION_EXPIRED,
  "authorization header must be bearer <token>": SESSION_EXPIRED,
  unauthorized: SESSION_EXPIRED,
  "insufficient permissions": INSUFFICIENT_PERMISSIONS,
  forbidden: INSUFFICIENT_PERMISSIONS,
  "store does not belong to client": INSUFFICIENT_PERMISSIONS,
  "too many requests": TOO_MANY_REQUESTS,
  "internal server error": INTERNAL_SERVER_ERROR,
};

export function getUserFacingErrorMessage(
  error: unknown,
  fallback?: string,
): string {
  if (!(error instanceof ApiError)) {
    return fallback || GENERIC_ERROR;
  }

  if (error.code) {
    const codeMessage = CODE_MESSAGES[error.code];
    if (codeMessage) return codeMessage;
  }

  const message = error.message.trim();
  const rawMessage = RAW_MESSAGES[message.toLowerCase()];
  if (rawMessage) return rawMessage;

  const sanitized = sanitizeApiMessage(message);
  if (sanitized !== message) return sanitized;

  if (error.code) {
    return `Não foi possível concluir a ação. Código: ${error.code}.`;
  }

  return fallback || GENERIC_ERROR;
}
